// CSS-filter looks — MVP approximation of the server's sharp matrices
// (RESEARCH/PHOTO_FILTERS_PARITY.md). Baked CDN colour-exact derivatives are
// the deferred escape hatch (PRD §2); this table is the client-side MVP path.
//
// CONTRACT (Quill, locked): Anvil emits the SERVER-RESOLVED effective filter
// id per page + cover — the client only applies it, never re-resolves scope.
// The id set mirrors the app's PhotoFilterId enum. Any id we don't recognise
// falls back to passthrough (never breaks a render) — so if Anvil's frozen
// enum is a subset, unknown values degrade gracefully to natural colour.
import type { FilterId } from "./manifest";

// sharp reference (parity doc) -> CSS approximation:
//   none   passthrough
//   mono   .greyscale()                         -> grayscale(1)
//   noir   .greyscale() then .linear(1.35,-45)  -> grayscale(1) contrast + slight darken
//   sepia  .recomb(SEPIA_RECOMB) @0.9           -> sepia(0.9)
//   fade   .modulate(sat .65, bri 1.08) .linear(.92,20)
//                                               -> saturate(.65) brightness(1.08) contrast(.92)
const CSS_FILTER: Record<FilterId, string> = {
  none: "none",
  mono: "grayscale(1)",
  noir: "grayscale(1) contrast(1.35) brightness(0.9)",
  sepia: "sepia(0.9)",
  fade: "saturate(0.65) brightness(1.08) contrast(0.92)",
};

/** CSS `filter` string for a resolved look id. Unknown/absent => passthrough. */
export function cssFilter(look: string | undefined | null): string {
  if (!look) return "none";
  const known = CSS_FILTER[look as FilterId];
  if (known === undefined) {
    // Anvil emitted an id outside our known set — render natural, don't crash.
    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.warn(`[filters] unknown look id "${look}" — rendering natural`);
    }
    return "none";
  }
  return known;
}
